import { useEffect, useState } from "react";
import { useGetRequestQuery } from "../../../../Hooks/useGetRequestQuery";
import DataTable from "react-data-table-component";
import { Button, Spinner, Modal, ModalHeader, ModalBody, ModalFooter, Form, Col } from "reactstrap";
import { useSafeNavigate } from "../../../../Hooks/useSafeNavigate";
import { getAllAreasService, deleteAreaService, createAreaService, updateAreaService } from "../../../../Services/CRMAdminServices/CRMArea";
import { toast } from "react-toastify";
import { useMutationRequestQuery } from "../../../../Hooks/useMutationRequestQuery";
import { useQueryClient } from "@tanstack/react-query";
import { useGetRequestQuery as useGetRequestQueryHook } from "../../../../Hooks/useGetRequestQuery";
import { getAllStates } from "../../../../Services/MMPClientServices/PropertySearchService";
import TextField from "../../../common/TextField";
import DropdownWithValueName from "../../../common/DropdownWithValueName";

const customStyles = {
    head: {
        style: {
            fontWeight: '800',
            color: '#0000ff',
            fontSize: '14px'
        },
    },
};

const CRMArea = () => {
    const [areas, setAreas] = useState<any[]>([]);
    const [deleteModal, setDeleteModal] = useState(false);
    const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null);
    const [areaToDelete, setAreaToDelete] = useState<any>(null);
    const [formErrors, setFormErrors] = useState<any>({});
    const [statesData, setStatesData] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [editingAreaId, setEditingAreaId] = useState<number | null>(null);
    const [formData, setFormData] = useState({
        areaName: "",
        stateId: "",
    });

    const queryClient = useQueryClient();
    const navigate = useSafeNavigate();

    const { data: areaData, isLoading, isError, refetch } = useGetRequestQuery<any>(
        ['getAllAreasService'],
        getAllAreasService,
        { enabled: true }
    );

    const {
        data: allStatesData,
        isLoading: allStatesLoading,
        isError: allStatesIsError,
    } = useGetRequestQueryHook(
        ["getAllStates"],
        getAllStates,
        {
            enabled: true,
        }
    );

    useEffect(() => {
        if (areaData && !isError && !isLoading) {
            console.log("Areas data loaded:", areaData?.data);
            setAreas(areaData?.data || []);
        }
    }, [areaData, isError, isLoading]);

    useEffect(() => {
        if (allStatesData?.data && !allStatesIsError && !allStatesLoading) {
            const transformedData = allStatesData.data.map((state: any) => ({
                value: state.id,
                label: state.name
            }));
            setStatesData(transformedData);
             allStatesData.data.forEach((state: any) => {
            console.log(`State Name: ${state.name}, State ID: ${state.id}`);
        });
        }
    }, [allStatesData, allStatesIsError, allStatesLoading]);

    const handleValueChange = (name: string, value: string) => {
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
        setFormErrors((prevErrors: any) => ({
            ...prevErrors,
            [name]: "",
        }));
    };

    const handleEdit = (area: any) => {
        setIsEditing(true);
        setEditingAreaId(area.areaId);
        setFormData({
            areaName: area.areaName,
            stateId: area.stateId.toString(),
        });
        setFormErrors({});
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditingAreaId(null);
        setFormData({ areaName: "", stateId: "" });
        setFormErrors({});
    };

    // Handle delete mutation
    const { mutate: deleteArea, isPending: isDeleting } = useMutationRequestQuery(
        "deleteArea",
        deleteAreaService,
        {
            onSuccess: (response: any) => {
                console.log("Delete success:", response);
                toast.success("Area deleted successfully.");
                setDeleteModal(false);
                setSelectedAreaId(null);
                setAreaToDelete(null);
                refetch();
            },
            onError: (error: any) => {
                console.error("Delete error details:", {
                    error,
                    response: error?.response,
                    status: error?.response?.status,
                    data: error?.response?.data,
                    message: error?.message
                });
                const errorMessage = error?.response?.data?.message || error?.message || "Failed to delete area.";
                toast.error(errorMessage);
            },
        }
    );

    // Mutation for adding an area
    const { mutate: addArea, isPending: isAddingArea } = useMutationRequestQuery(
        "createAreaService",
        createAreaService,
        {
            onSuccess: (response: any) => {
                console.log("Add success:", response);
                toast.success("Area added successfully.");
                setFormData({ areaName: "", stateId: "" });
                refetch();
            },
            onError: (error: any) => {
                console.error("Add error:", error);
                toast.error("Failed to add area.");
            },
        }
    );

    // Mutation for updating an area
    const { mutate: updateArea, isPending: isUpdatingArea } = useMutationRequestQuery(
        "updateAreaService",
        updateAreaService,
        {
            onSuccess: (response: any) => {
                console.log("Update success:", response);
                toast.success("Area updated successfully.");
                setIsEditing(false);
                setEditingAreaId(null);
                setFormData({ areaName: "", stateId: "" });
                refetch();
            },
            onError: (error: any) => {
                console.error("Update error:", error);
                toast.error("Failed to update area.");
            },
        }
    );

    // Handle delete confirmation
    const confirmDelete = (area: any) => {
        console.log("Confirming delete for area:", area);
        console.log("Area ID:", area.areaId || area.id);
        setAreaToDelete(area);
        setSelectedAreaId(area.areaId || area.id);
        setDeleteModal(true);
    };

    // Execute delete operation
    const handleDeleteClick = () => {
        console.log("handleDeleteClick called");
        console.log("areaToDelete:", areaToDelete);
        console.log("selectedAreaId:", selectedAreaId);
        
        const areaId = areaToDelete?.areaId || areaToDelete?.id || selectedAreaId;
        console.log("Final areaId to delete:", areaId);
        
        if (areaId) {
            console.log("Deleting area with ID:", areaId);
            try {
                deleteArea(areaId);
            } catch (error) {
                console.error("Error in handleDeleteClick:", error);
                toast.error("An error occurred while deleting the area.");
            }
        } else {
            console.error("No area ID found for deletion");
            console.log("areaToDelete:", areaToDelete);
            console.log("selectedAreaId:", selectedAreaId);
            toast.error("No area selected for deletion.");
        }
    };

    // Handle form submission
    const handleSubmit = (e: any) => {
        e.preventDefault();
        let errors: any = {};
        if (!formData.areaName) errors.areaName = "Area Name is required!";
        if (!formData.stateId) errors.stateId = "State is required!";
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        if (isEditing && editingAreaId) {
            const updateData = { 
                areaId: editingAreaId, 
                areaName: formData.areaName, 
                stateId: Number(formData.stateId) 
            };
            console.log("Updating area with data:", updateData);
            updateArea(updateData);
        } else {
            const addData = { 
                areaName: formData.areaName, 
                stateId: Number(formData.stateId) 
            };
            console.log("Adding area with data:", addData);
            addArea(addData);
        }
    };

    // Function to get state name by state ID
    const getStateNameById = (stateId: number) => {
        const state = statesData.find((state: any) => state.value === stateId) as any;
        return state ? state.label : `State ID: ${stateId}`;
    };

    // Table columns
    const columns = [
        {
            name: "Action",
            cell: (row: any) => {
                // console.log("Action column row data:", row);
                // console.log("Available fields:", Object.keys(row));
                // console.log("areaId value:", row.areaId);
                // console.log("id value:", row.id);
                return (
                    <ul className="action simple-list flex-row">
                        <li className="edit">
                            <a onClick={() => handleEdit(row)} href="#">
                                <i className="icon-pencil-alt" />
                            </a>
                        </li>
                        <li className="delete">
                            <a onClick={() => confirmDelete(row)} href="#">
                                <i className="icon-trash" />
                            </a>
                        </li>
                    </ul>
                );
            },
            width: '6rem'
        },
        // {
        //     name: "Area ID",
        //     selector: (row: any) => row.areaId,
        //     sortable: false,
        //     width: "8rem"
        // },
        {
            name: "Area Name",
            selector: (row: any) => row.areaName,
            sortable: false,
            width: "25rem"
        },
        {
            name: "State Name",
            cell: (row: any) => getStateNameById(row.stateId),
            sortable: false,
            width: "12rem"
        }
    ];

    return (
        <>
            <div className="px-4 pt-4" style={{ minHeight: '500px', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                <div style={{ borderRadius: '8px', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', padding: '10px', backgroundColor: '#fff' }}>
                    <div className="g-3 mb-2 d-flex justify-content-between align-items-center">
                        <h3 className="f-w-900 heading-color mx-3 py-2">Area List</h3>
                    </div>

                    {/* Add/Edit Area Form */}
                    <div style={{ borderRadius: '8px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', padding: '15px', backgroundColor: '#f8f9fa', marginBottom: '20px' }}>
                        <h5 className="mb-3">{isEditing ? 'Edit Area' : 'Add New Area'}</h5>
                        <Form onSubmit={handleSubmit}>
                            <div className="row">
                                <Col md={3} className="mb-3">
                                    <TextField
                                        label="Area Name"
                                        type="text"
                                        name="areaName"
                                        value={formData.areaName}
                                        setValue={handleValueChange}
                                        error={formErrors.areaName}
                                    />
                                </Col>
                                <Col md={2} className="mb-3">
                                    <DropdownWithValueName
                                        label="State"
                                        name="stateId"
                                        value={formData.stateId}
                                        setValue={handleValueChange}
                                        options={statesData}
                                        error={formErrors.stateId}
                                    />
                                </Col>
                                <Col md={4} className="mb-3 d-flex align-items-center gap-2">
                                    <Button color="primary" type="submit" disabled={isAddingArea || isUpdatingArea}>
                                        {isAddingArea ? "Adding..." : isUpdatingArea ? "Updating..." : isEditing ? "Update Area" : "Add Area"}
                                    </Button>
                                    {isEditing && (
                                        <Button color="secondary" onClick={handleCancelEdit}>
                                            Cancel
                                        </Button>
                                    )}

                                  <Button color="success" onClick={() => navigate("/CRMSuburb")}>Back</Button>

                                </Col>
                            </div>
                        </Form>
                    </div>

                    {isLoading ? (
                        <div
                            style={{
                                borderRadius: '8px',
                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                padding: '12px',
                                backgroundColor: '#fff',
                                textAlign: 'center',
                            }}
                            className='mt-4 mb-2'
                        >
                            <Spinner color="primary" />
                        </div>
                    ) : isError ? (
                        <div
                            style={{
                                borderRadius: '8px',
                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                padding: '12px',
                                backgroundColor: '#fff',
                                textAlign: 'center',
                            }}
                            className='mt-4 mb-2'
                        >
                            <h6 className="text-danger">Error loading areas. Please try again.</h6>
                        </div>
                    ) : areas.length !== 0 ? (
                        <DataTable
                            columns={columns}
                            data={areas}
                            customStyles={customStyles}
                            responsive
                        />
                    ) : (
                        <div
                            style={{
                                borderRadius: '8px',
                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                padding: '12px',
                                backgroundColor: '#fff',
                                textAlign: 'center',
                            }}
                            className='mt-4 mb-2'
                        >
                            <h6>No area data available.</h6>
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            <Modal isOpen={deleteModal} toggle={() => {
                setDeleteModal(!deleteModal);
                setSelectedAreaId(null);
                setAreaToDelete(null);
            }} centered>
                <ModalHeader toggle={() => {
                    setDeleteModal(!deleteModal);
                    setSelectedAreaId(null);
                    setAreaToDelete(null);
                }}>Confirm Delete</ModalHeader>
                <ModalBody>
                    Are you sure you want to delete this area?
                    {/* {areaToDelete && (
                        <div className="mt-2">
                            <strong>Area Name:</strong> {areaToDelete.areaName}
                            <br />
                            <strong>Area ID:</strong> {areaToDelete.areaId || areaToDelete.id}
                        </div>
                    )} */}
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => {
                        setDeleteModal(false);
                        setSelectedAreaId(null);
                        setAreaToDelete(null);
                    }}>Cancel</Button>
                    <Button color="danger" onClick={handleDeleteClick} disabled={isDeleting}>
                        {isDeleting ? "Deleting..." : "Delete"}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default CRMArea; 